import { useTranslation } from 'react-i18next'
import { HugeiconsIcon } from '@hugeicons/react'
import { AlertCircleIcon, Delete02Icon, File01Icon } from '@hugeicons-pro/core-stroke-rounded'
import { Box, Button, CircularProgress, IconButton, LinearProgress, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import PropTypes from 'prop-types'

import TbxTooltip from '@/common/components/TbxTooltip/TbxTooltip'

import { ATTACHMENT_ROW_COLUMNS } from './AttachmentRow'
import { formatBytes } from './formatBytes'
import { UPLOAD_STATE } from './useAttachmentUploads'

// Copied from the webapp's UploadRow, minus the visibility cell (the web store has no such column).

/** A state label and the file size, side by side under the name. */
const StateLine = ({ children, color = 'text.secondary', size }) => (
    <>
        <Typography
            color={color}
            variant="caption"
        >
            {children}
        </Typography>
        <Typography
            color="text.secondary"
            variant="caption"
        >
            · {size}
        </Typography>
    </>
)

StateLine.propTypes = {
    children: PropTypes.node.isRequired,
    size: PropTypes.string.isRequired,
    color: PropTypes.string,
}

/**
 * UploadRow - A file on its way in, drawn as a row of the documents table so it keeps its place
 * from the moment it is picked. Its state, progress and any reason sit under the name, so they
 * stay in view when a phone scrolls the table sideways; the uploaded and status cells stay empty
 * until the file is stored. Retry, remove and dismiss sit in the actions column, where the stored
 * rows keep theirs.
 *
 * @param {Object} props
 * @param {number} props.freeSlots
 * @param {string} props.id - DOM id, so the header can scroll to the row
 * @param {boolean} props.inFlight - A batch is uploading; every control waits for it
 * @param {Object} props.item - Queue entry from useAttachmentUploads
 * @param {Function} props.onRemove - (key) => void
 * @param {Function} props.onRetry - (key) => void
 */
const UploadRow = ({ freeSlots, id, inFlight, item, onRemove, onRetry }) => {
    const { t } = useTranslation()
    const { accepted, canRetry, file, key, message, progress, state, tooLarge } = item
    const isError = state === UPLOAD_STATE.error
    const isIdle = state === UPLOAD_STATE.held || state === UPLOAD_STATE.waiting
    const size = formatBytes(file.size)

    const canRemove = state === UPLOAD_STATE.held || isError
    // A greyed-out button with no reason is a dead end; say why on hover.
    const waitReason = t('Wait for the current upload to finish.')
    const retryBlockedReason = inFlight
        ? waitReason
        : freeSlots === 0
          ? t('No free slots. Remove an attachment first.')
          : ''

    return (
        <Box
            aria-label={file.name}
            component="li"
            id={id}
            sx={{
                alignItems: 'center',
                backgroundColor: (theme) => (isError ? alpha(theme.palette.error.main, 0.06) : 'transparent'),
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                borderRadius: isError ? 1 : 0,
                display: 'grid',
                gap: 2,
                gridTemplateColumns: ATTACHMENT_ROW_COLUMNS,
                listStyle: 'none',
                px: 1.5,
                py: 1.25,
            }}
        >
            <Box sx={{ alignItems: 'flex-start', display: 'flex', gap: 1.5, minWidth: 0 }}>
                <Box
                    component="span"
                    sx={{
                        color: isError ? 'error.main' : 'action.active',
                        display: 'inline-flex',
                        opacity: isIdle ? 0.55 : 1,
                        pt: 0.25,
                    }}
                >
                    <HugeiconsIcon
                        icon={isError ? AlertCircleIcon : File01Icon}
                        size={22}
                    />
                </Box>
                <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
                    <Typography
                        fontWeight={500}
                        sx={{ opacity: isIdle ? 0.55 : 1 }}
                        title={file.name}
                        variant="body2"
                        noWrap
                    >
                        {file.name}
                    </Typography>

                    <Box sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {state === UPLOAD_STATE.held ? <StateLine size={size}>{t('Not sent yet')}</StateLine> : null}
                        {state === UPLOAD_STATE.waiting ? (
                            <StateLine size={size}>{t('Waiting to upload')}</StateLine>
                        ) : null}
                        {state === UPLOAD_STATE.uploading ? (
                            <>
                                <LinearProgress
                                    aria-label={t('Uploading {{name}}', { name: file.name })}
                                    sx={{ width: 140 }}
                                    value={progress}
                                    variant="determinate"
                                />
                                <StateLine size={size}>{t('Uploading {{percent}}%', { percent: progress })}</StateLine>
                            </>
                        ) : null}
                        {state === UPLOAD_STATE.checking ? (
                            <>
                                <CircularProgress
                                    aria-label={t('Checking {{name}}', { name: file.name })}
                                    color="secondary"
                                    size={14}
                                    thickness={5}
                                    disableShrink
                                />
                                <StateLine
                                    color="secondary"
                                    size={size}
                                >
                                    {t('Checking file…')}
                                </StateLine>
                            </>
                        ) : null}
                        {state === UPLOAD_STATE.done ? (
                            <StateLine
                                color={accepted ? 'success.main' : 'text.secondary'}
                                size={size}
                            >
                                {accepted ? t('Uploaded') : t('Sent. The list will show the result.')}
                            </StateLine>
                        ) : null}
                        {isError ? (
                            <Typography
                                color="error"
                                variant="caption"
                            >
                                {message}
                            </Typography>
                        ) : null}
                    </Box>
                </Box>
            </Box>

            {/* Uploaded and status mean nothing until the file is stored; the state is under the name. */}
            <span />
            <span />

            <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                {/* The spans keep the tooltips working while the buttons are disabled. The theme
                    uppercases buttons; Retry and Dismiss turn that off to read as plain words. */}
                {isError && canRetry ? (
                    <TbxTooltip title={retryBlockedReason}>
                        <span>
                            <Button
                                aria-label={t('Retry {{name}}', { name: file.name })}
                                disabled={Boolean(retryBlockedReason)}
                                size="small"
                                sx={{ minWidth: 0, textTransform: 'none' }}
                                onClick={() => onRetry(key)}
                            >
                                {t('Retry')}
                            </Button>
                        </span>
                    </TbxTooltip>
                ) : null}
                {tooLarge ? (
                    <TbxTooltip title={inFlight ? waitReason : ''}>
                        <span>
                            <Button
                                aria-label={t('Dismiss {{name}}', { name: file.name })}
                                color="error"
                                disabled={inFlight}
                                size="small"
                                sx={{ textTransform: 'none' }}
                                onClick={() => onRemove(key)}
                            >
                                {t('Dismiss')}
                            </Button>
                        </span>
                    </TbxTooltip>
                ) : null}
                {canRemove && !tooLarge ? (
                    <TbxTooltip title={inFlight ? waitReason : t('Remove from the upload list')}>
                        <span>
                            <IconButton
                                aria-label={t('Remove {{name}} from the upload list', { name: file.name })}
                                disabled={inFlight}
                                size="small"
                                onClick={() => onRemove(key)}
                            >
                                <HugeiconsIcon
                                    icon={Delete02Icon}
                                    size={20}
                                />
                            </IconButton>
                        </span>
                    </TbxTooltip>
                ) : null}
            </Box>
        </Box>
    )
}

UploadRow.propTypes = {
    freeSlots: PropTypes.number.isRequired,
    id: PropTypes.string.isRequired,
    inFlight: PropTypes.bool.isRequired,
    item: PropTypes.shape({
        file: PropTypes.object.isRequired,
        key: PropTypes.number.isRequired,
        state: PropTypes.string.isRequired,
        accepted: PropTypes.bool,
        canRetry: PropTypes.bool,
        message: PropTypes.string,
        progress: PropTypes.number,
        tooLarge: PropTypes.bool,
    }).isRequired,
    onRemove: PropTypes.func.isRequired,
    onRetry: PropTypes.func.isRequired,
}

export default UploadRow
